import React, { useMemo, useState } from 'react';
import {
    Accordion,
    Badge,
    Box,
    Group,
    Loader,
    SegmentedControl,
    Stack,
    Text,
    ThemeIcon,
    Timeline,
} from '@mantine/core';
import {
    IconEdit,
    IconLock,
    IconLockOpen,
    IconPlus,
    IconTrash,
    IconHistory,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { IPrescriptionActivityEvent } from '../../../../global/entities/Prescription/Prescription';
import { usePrescriptionActivityHistory } from '../../../../global/entities/Prescription/queries/usePrescriptionActivityHistory';

interface PrescriptionActivityHistoryProps {
    patientId: string | number | undefined;
}

type FilterValue = 'all' | 'validation' | 'unlock' | 'edits';

const ACTION_CONFIG: Record<string, { icon: React.ElementType; color: string }> = {
    Created: { icon: IconPlus, color: 'blue' },
    Edited: { icon: IconEdit, color: 'gray' },
    Deleted: { icon: IconTrash, color: 'red' },
    Validated: { icon: IconLock, color: 'green' },
    Unlocked: { icon: IconLockOpen, color: 'orange' },
};

function formatRelative(date: Date, lang: string): string {
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.round(diffMs / 60000);
    const rtf = new Intl.RelativeTimeFormat(lang === 'fr' ? 'fr-CA' : 'en-CA', { numeric: 'auto' });
    if (Math.abs(diffMin) < 60) return rtf.format(-diffMin, 'minute');
    const diffHours = Math.round(diffMin / 60);
    if (Math.abs(diffHours) < 24) return rtf.format(-diffHours, 'hour');
    const diffDays = Math.round(diffHours / 24);
    if (Math.abs(diffDays) < 30) return rtf.format(-diffDays, 'day');
    const diffMonths = Math.round(diffDays / 30);
    return rtf.format(-diffMonths, 'month');
}

function matchesFilter(action: string, filter: FilterValue): boolean {
    switch (filter) {
        case 'all':
            return true;
        case 'validation':
            return action === 'Validated';
        case 'unlock':
            return action === 'Unlocked';
        case 'edits':
            return action === 'Created' || action === 'Edited' || action === 'Deleted';
        default:
            return true;
    }
}

function describeEvent(event: IPrescriptionActivityEvent, t: (k: string, vars?: Record<string, unknown>) => string): string {
    const details = event.details ?? {};
    const drugName = (details.drugName as string) || '';
    switch (event.action) {
        case 'Created':
            return t('prescriptionHistory.activity.descriptions.created', { drugName });
        case 'Deleted':
            return t('prescriptionHistory.activity.descriptions.deleted', { drugName });
        case 'Validated':
            return t('prescriptionHistory.activity.descriptions.validated', { drugName });
        case 'Unlocked': {
            const reasonCode = details.reasonCode as string | undefined;
            const reasonLabel = reasonCode
                ? t(`prescriptionHistory.unlock.reason.${reasonCode}`)
                : '';
            return t('prescriptionHistory.activity.descriptions.unlocked', { drugName, reason: reasonLabel });
        }
        case 'Edited': {
            const field = details.field as string | undefined;
            const fieldLabel = field ? t(`prescriptionHistory.activity.fields.${field}`) : field ?? '';
            const fromRaw = details.oldValue;
            const toRaw = details.newValue;
            const from = fromRaw === null || fromRaw === undefined ? '—' : String(fromRaw);
            const to = toRaw === null || toRaw === undefined ? '—' : String(toRaw);
            return t('prescriptionHistory.activity.descriptions.edited', {
                drugName,
                field: fieldLabel,
                from,
                to,
            });
        }
        default:
            return event.action;
    }
}

export const PrescriptionActivityHistory: React.FC<PrescriptionActivityHistoryProps> = ({ patientId }) => {
    const { t, i18n } = useTranslation();
    const { data: events, isLoading } = usePrescriptionActivityHistory(patientId);
    const [filter, setFilter] = useState<FilterValue>('all');

    const filtered = useMemo(() => {
        if (!events) return [];
        return events.filter((e) => matchesFilter(e.action, filter));
    }, [events, filter]);

    const formatAbsolute = (iso: string): string =>
        new Date(iso).toLocaleString(i18n.language === 'fr' ? 'fr-CA' : 'en-CA', {
            dateStyle: 'medium',
            timeStyle: 'short',
        });

    return (
        <Accordion variant="separated" defaultValue={null}>
            <Accordion.Item value="activity">
                <Accordion.Control icon={<IconHistory size={18} />}>
                    <Group gap="xs">
                        <Text fw={600}>{t('prescriptionHistory.activity.title')}</Text>
                        {events && events.length > 0 && (
                            <Badge variant="light" color="gray">
                                {events.length}
                            </Badge>
                        )}
                    </Group>
                </Accordion.Control>
                <Accordion.Panel>
                    {isLoading ? (
                        <Loader size="sm" />
                    ) : !events || events.length === 0 ? (
                        <Text size="sm" c="dimmed">
                            {t('prescriptionHistory.activity.empty')}
                        </Text>
                    ) : (
                        <Stack gap="md">
                            <SegmentedControl
                                size="xs"
                                value={filter}
                                onChange={(v) => setFilter(v as FilterValue)}
                                data={[
                                    { value: 'all', label: t('prescriptionHistory.activity.filters.all') },
                                    { value: 'validation', label: t('prescriptionHistory.activity.filters.validation') },
                                    { value: 'unlock', label: t('prescriptionHistory.activity.filters.unlock') },
                                    { value: 'edits', label: t('prescriptionHistory.activity.filters.edits') },
                                ]}
                            />
                            {filtered.length === 0 ? (
                                <Text size="sm" c="dimmed">
                                    {t('prescriptionHistory.activity.emptyForFilter')}
                                </Text>
                            ) : (
                                <Timeline bulletSize={22} lineWidth={2} active={-1}>
                                    {filtered.map((event) => {
                                        const config = ACTION_CONFIG[event.action] ?? {
                                            icon: IconHistory,
                                            color: 'gray',
                                        };
                                        const Icon = config.icon;
                                        const eventDate = new Date(event.eventDate);
                                        return (
                                            <Timeline.Item
                                                key={event.id}
                                                bullet={
                                                    <ThemeIcon size={22} color={config.color} radius="xl" variant="filled">
                                                        <Icon size={12} />
                                                    </ThemeIcon>
                                                }
                                                title={
                                                    <Text size="sm" fw={500}>
                                                        {describeEvent(event, t)}
                                                    </Text>
                                                }
                                            >
                                                <Text size="xs" c="dimmed">
                                                    {event.actorName ? `${event.actorName} · ` : ''}
                                                    {formatAbsolute(event.eventDate)}
                                                    {' · '}
                                                    {formatRelative(eventDate, i18n.language)}
                                                </Text>
                                                {event.action === 'Unlocked' && event.details?.note ? (
                                                    <Box mt={4}>
                                                        <Text size="xs" c="dimmed" fs="italic">
                                                            {String(event.details.note)}
                                                        </Text>
                                                    </Box>
                                                ) : null}
                                            </Timeline.Item>
                                        );
                                    })}
                                </Timeline>
                            )}
                        </Stack>
                    )}
                </Accordion.Panel>
            </Accordion.Item>
        </Accordion>
    );
};
